/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/custom/CountrySelectField.tsx
 * Release: 1.3.1
 * Descrizione: Select custom con bandierine SVG e modale di conferma per paesi EU
 */

import React, { useState, useRef, useEffect } from 'react';

interface Option {
  value: string;
  label: string;
  flag?: string;
}

interface CountrySelectFieldProps {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  disabled?: boolean;
  placeholder?: string;
  tabIndex?: number;
}

// Componente per renderizzare la bandiera
const FlagIcon: React.FC<{ countryCode: string; className?: string }> = ({ 
  countryCode, 
  className = "w-5 h-4" 
}) => {
  return (
    <img
      src={`https://flagcdn.com/${countryCode.toLowerCase()}.svg`}
      alt={`Bandiera ${countryCode}`}
      className={`${className} rounded-sm object-cover`}
      onError={(e) => {
        // Fallback se l'immagine non carica
        const target = e.target as HTMLImageElement;
        target.style.display = 'none';
      }}
    />
  );
};

// Modale di conferma
const ConfirmationModal: React.FC<{
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  selectedCountry: Option | null;
}> = ({ isOpen, onConfirm, onCancel, selectedCountry }) => {
  if (!isOpen || !selectedCountry) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-zinc-800 border border-zinc-600 rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center mb-4">
          <FlagIcon countryCode={selectedCountry.flag || selectedCountry.value} className="w-8 h-6 mr-3" />
          <h3 className="text-lg font-semibold text-white">Conferma Selezione</h3>
        </div>
        
        <p className="text-zinc-300 mb-6">
          Hai selezionato il prefisso per il paese <strong>{selectedCountry.label}</strong>. Sei sicuro?
        </p>
        
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-zinc-600 text-white rounded-lg hover:bg-zinc-500 transition-colors"
          >
            No, torna all'Italia
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
          >
            Sì, conferma
          </button>
        </div>
      </div>
    </div>
  );
};

export const CountrySelectField: React.FC<CountrySelectFieldProps> = ({
  value,
  onChange,
  options,
  disabled = false,
  placeholder = 'Seleziona...',
  tabIndex
}) => {
  console.log(`🔧 [CountrySelectField] RENDERING:`, {
    value,
    options: options?.length,
    disabled,
    placeholder,
    DEBUG_TIMESTAMP: new Date().toISOString()
  });
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<Option | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Funzione per calcolare la posizione del dropdown
  const calculateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const position = {
        top: rect.bottom + 5, // 5px di spazio dal bottone
        left: rect.left,
        width: rect.width
      };
      setDropdownPosition(position);
    }
  };

  // Non impostare automaticamente il default - lascia che sia gestito dal form
  // Il valore di default viene gestito dal form stesso quando non ci sono dati esistenti

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calcola la posizione del dropdown quando si apre
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [isOpen]);

  // Listener per lo scroll che ricalcola la posizione del dropdown
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    // Aggiungi listener per scroll su window e su elementi scrollabili
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  // Listener per il resize della finestra
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  // Trova l'opzione selezionata
  const selectedOption = options.find(opt => opt.value === value);
  
  // Filtra opzioni in base alla ricerca
  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    opt.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (optionValue: string) => {
    const selectedOption = options.find(opt => opt.value === optionValue);
    
    // Se si seleziona un paese diverso dall'Italia, mostra la modale
    if (optionValue !== 'IT' && value === 'IT') {
      setPendingSelection(selectedOption || null);
      setShowModal(true);
      setIsOpen(false);
      setSearchTerm('');
    } else {
      // Se si seleziona l'Italia o si conferma, cambia direttamente
      onChange(optionValue);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleConfirmSelection = () => {
    if (pendingSelection) {
      onChange(pendingSelection.value);
    }
    setShowModal(false);
    setPendingSelection(null);
  };

  const handleCancelSelection = () => {
    setShowModal(false);
    setPendingSelection(null);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  // Handler per gestire il focus e la navigazione tab
  const handleFocus = () => {
    // Focus handler per accessibilità
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleDropdown();
    }
  };


  return (
    <>
      <div className="relative w-full" ref={dropdownRef}>

        {/* Bottone principale */}
        <button
          ref={buttonRef}
          type="button"
          tabIndex={tabIndex || 1}
          onClick={toggleDropdown}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className={`
            w-full px-3 py-2 border rounded-lg bg-zinc-700 text-white 
            focus:outline-none focus:ring-2 focus:ring-blue-500 border-zinc-600 
            placeholder-zinc-400 transition-colors duration-200
            flex items-center justify-between
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-zinc-600'}
          `}
        >
          <div className="flex items-center">
            {selectedOption ? (
              <>
                <FlagIcon 
                  countryCode={selectedOption.flag || selectedOption.value} 
                  className="w-5 h-4 mr-2"
                />
                <span className="text-sm text-zinc-200">
                  {selectedOption.label}
                </span>
              </>
            ) : (
              <span className="text-zinc-400">{placeholder}</span>
            )}
          </div>
          <svg 
            className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        
        
        {/* Dropdown */}
        {isOpen && (
                      <div 
              className="fixed z-[999999] bg-zinc-700 border border-zinc-600 rounded-lg shadow-lg max-h-60 overflow-hidden"
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                position: 'fixed',
                zIndex: 999999,
                backgroundColor: '#3f3f46',
                border: '1px solid #52525b',
                borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
                maxHeight: '240px',
                overflow: 'hidden'
              }}
            >
              

              {/* Barra di ricerca */}
            <div className="p-3 border-b border-zinc-600">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Cerca paese..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 bg-zinc-600 border border-zinc-500 rounded-md text-white placeholder-zinc-400 text-sm focus:outline-none focus:border-blue-400"
                  autoFocus
                />
              </div>
            </div>

            {/* Lista opzioni */}
            <div className="max-h-48 overflow-y-auto">

              
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={`
                      flex items-center gap-3 w-full px-3 py-2 text-left 
                      hover:bg-zinc-600 transition-colors duration-200
                      ${option.value === value ? 'bg-blue-600 text-white' : 'text-white'}
                    `}
                  >
                    <FlagIcon 
                      countryCode={option.flag || option.value} 
                      className="w-5 h-4"
                    />
                    <span className="text-sm">
                      {option.label}
                    </span>
                    {option.value === value && (
                      <svg className="ml-auto w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-center text-zinc-400 text-sm">
                  Nessun paese trovato
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modale di conferma */}
      <ConfirmationModal
        isOpen={showModal}
        onConfirm={handleConfirmSelection}
        onCancel={handleCancelSelection}
        selectedCountry={pendingSelection}
      />
    </>
  );
};

export default CountrySelectField; 